import { Helmet } from "react-helmet-async";
import { useContent } from "./ContentContext";
import { BRAND } from "./data";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

const abs = (url) => {
  if (!url) return "";
  if (/^https?:/i.test(url)) return url;
  if (url.startsWith("/api")) return BACKEND + url;
  return origin() + url;
};

const DAY_FULL = { Su: "Sunday", Mo: "Monday", Tu: "Tuesday", We: "Wednesday", Th: "Thursday", Fr: "Friday", Sa: "Saturday" };
const CODE = { sun: "Su", mon: "Mo", tue: "Tu", wed: "We", thu: "Th", fri: "Fr", sat: "Sa" };
const ORDER = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const dayCode = (s) => CODE[(s || "").trim().toLowerCase().slice(0, 3)];

const expandDays = (label) => {
  const parts = (label || "").split(/[—–-]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 1) { const c = dayCode(parts[0]); return c ? [c] : []; }
  const a = dayCode(parts[0]); const b = dayCode(parts[1]);
  if (!a || !b) return [];
  const res = []; let i = ORDER.indexOf(a); const j = ORDER.indexOf(b);
  for (let n = 0; n < 7; n++) { res.push(ORDER[i]); if (ORDER[i] === ORDER[j]) break; i = (i + 1) % 7; }
  return res;
};

const to24 = (t) => {
  const m = (t || "").trim().match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)/i);
  if (!m) return null;
  let h = +m[1]; const min = m[2] || "00"; const ap = m[3].toUpperCase();
  if (ap === "PM" && h !== 12) h += 12; if (ap === "AM" && h === 12) h = 0;
  return String(h).padStart(2, "0") + ":" + min;
};

const parseRange = (time) => {
  const parts = (time || "").split(/[—–-]/).map((s) => s.trim());
  if (parts.length < 2) return null;
  const opens = to24(parts[0]); const closes = to24(parts[1]);
  return opens && closes ? { opens, closes } : null;
};

const buildOpeningHours = (hours) =>
  (hours || [])
    .map((h) => {
      const days = expandDays(h.day).map((c) => DAY_FULL[c]).filter(Boolean);
      const r = parseRange(h.time);
      if (!days.length || !r) return null;
      return { "@type": "OpeningHoursSpecification", dayOfWeek: days, opens: r.opens, closes: r.closes };
    })
    .filter(Boolean);

const parseAddress = (addr) => {
  const parts = (addr || "").split(",").map((s) => s.trim());
  const street = parts[0] || "";
  const city = parts[1] || "";
  const sz = (parts[2] || "").split(/\s+/);
  return { street, city, region: sz[0] || "", postal: sz[1] || "" };
};

export const Seo = ({ page = "home", title, description, path = "/", noindex = false }) => {
  const { settings } = useContent();
  const seo = settings.seo || {};

  const defaults = {
    home: ["Blue Tequila | Mexican Restaurant, Grill & Cantina", ""],
    menu: ["Full Menu | Blue Tequila", ""],
    drinks: ["Bar Menu | Blue Tequila", ""],
  };
  const pick = {
    home: [seo.title, seo.description],
    menu: [seo.menuTitle, seo.menuDescription],
    drinks: [seo.drinksTitle, seo.drinksDescription],
  }[page] || [seo.title, seo.description];

  const t = title || pick[0] || defaults[page]?.[0] || "Blue Tequila";
  const d = description || pick[1] || seo.description || "";
  const siteName = seo.siteName || BRAND.name || "Blue Tequila";

  const base = (seo.canonicalBase || "").replace(/\/$/, "");
  const url = (base || origin()) + path;
  const img = abs(seo.ogImage);
  const ogTitle = seo.ogTitle || t;
  const ogDesc = seo.ogDescription || d;
  const themeColor = seo.themeColor || "#071847";
  const hidden = noindex || seo.searchVisible === false;
  const robots = hidden ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  const phone = settings.phone || BRAND.phone;
  const addr = parseAddress(settings.address || BRAND.address);
  const socials = [
    BRAND.facebook, seo.instagram, seo.xUrl, seo.yelp, seo.tripadvisor,
  ].filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "@id": (base || origin()) + "/#restaurant",
    name: seo.businessName || BRAND.name,
    image: img || undefined,
    url: (base || origin()) + "/",
    telephone: phone,
    priceRange: seo.priceRange || "$$",
    servesCuisine: seo.cuisine || "Mexican",
    address: {
      "@type": "PostalAddress",
      streetAddress: addr.street,
      addressLocality: addr.city,
      addressRegion: addr.region,
      postalCode: addr.postal,
      addressCountry: "US",
    },
    geo: (seo.geoLat && seo.geoLng) ? { "@type": "GeoCoordinates", latitude: seo.geoLat, longitude: seo.geoLng } : undefined,
    openingHoursSpecification: buildOpeningHours(settings.hours),
    hasMenu: (base || origin()) + "/menu",
    acceptsReservations: !!seo.acceptsReservations,
    sameAs: socials.length ? socials : undefined,
  };

  return (
    <Helmet prioritizeSeoTags>
      <title>{t}</title>
      <meta name="description" content={d} />
      {seo.keywords ? <meta name="keywords" content={seo.keywords} /> : null}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="theme-color" content={themeColor} />
      <link rel="canonical" href={url} />
      {seo.googleVerification ? <meta name="google-site-verification" content={seo.googleVerification} /> : null}
      {seo.bingVerification ? <meta name="msvalidate.01" content={seo.bingVerification} /> : null}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:url" content={url} />
      {img ? <meta property="og:image" content={img} /> : null}
      {img ? <meta property="og:image:alt" content={ogTitle} /> : null}
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content={img ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDesc} />
      {img ? <meta name="twitter:image" content={img} /> : null}
      {seo.twitterHandle ? <meta name="twitter:site" content={seo.twitterHandle} /> : null}
      {seo.twitterHandle ? <meta name="twitter:creator" content={seo.twitterHandle} /> : null}

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};
